import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TbLogin2 } from "react-icons/tb";
import { FaCheckCircle } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { GoogleLogin } from "@react-oauth/google";
import useElementWidth from "../hooks/useElementWidth";
import { useLanguage } from "../context/LanguageContext";
import { useToaster } from "../context/ToasterContext";
import Services from "../services/Services";
import { generateSecurePassword } from "../utils/passwordGenerator";
import "./AuthPages.css";

function resolvePostAuthPath(locationState, fallback = "/") {
  const returnTo = String(locationState?.returnTo || "").trim();
  if (returnTo.startsWith("/")) {
    return returnTo;
  }

  const from = String(locationState?.from || "").trim();
  if (!from.startsWith("/") || from === "/login" || from === "/register") {
    return fallback;
  }

  const fromSearch = typeof locationState?.fromSearch === "string" ? locationState.fromSearch : "";
  const fromHash = typeof locationState?.fromHash === "string" ? locationState.fromHash : "";
  return `${from}${fromSearch}${fromHash}`;
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login } = useAuth();
  const { getTranslation, language, setLanguage, availableLanguages } = useLanguage();
  const { toastError, toastSuccess } = useToaster();
  const postAuthPath = useMemo(() => resolvePostAuthPath(location.state, "/"), [location.state]);

  const [socialRef, socialWidth] = useElementWidth(400);
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [isTokenLoading, setIsTokenLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(postAuthPath, { replace: true });
    }
  }, [isAuthenticated, navigate, postAuthPath]);

  // Get anonymous token for API calls (only once on page load)
  useEffect(() => {
    setIsTokenLoading(true);
    Services.GetToken()
      .then((res) => {
        if (res.data.status) {
          setToken(res.data.result.token);
          Services.CreatePageView(res.data.result.token, "register").catch(() => {});
        }
      })
      .catch((err) => {
        console.error("GetToken error:", err);
      })
      .finally(() => {
        setIsTokenLoading(false);
      });
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!name.trim() || !lastname.trim() || !email.trim()) {
      toastError(getTranslation("somethingWentWrong"));
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toastError(getTranslation("invalidCredentials"));
      return;
    }

    if (!token) {
      toastError(getTranslation("somethingWentWrong"));
      return;
    }

    setIsLoading(true);

    try {
      // Generate a secure random password
      const generatedPassword = generateSecurePassword(14);

      const res = await Services.UserRegister(token, name.trim(), lastname.trim(), email.trim(), generatedPassword);

      if (res.data.status) {
        try {
          const loginRes = await Services.UserLogin(token, email.trim(), generatedPassword);
          if (loginRes?.data?.status) {
            const userData = loginRes.data.result;
            const userToken = userData?.token || userData?.accessToken || userData?.authToken || userData?.userToken;
            if (userToken) {
              login(userData, userToken, true);
              navigate(postAuthPath, { replace: true });
              return;
            }
          }
        } catch (err) {
          console.error("Auto-login after register error:", err);
        }

        setIsSuccess(true);
        toastSuccess(getTranslation("registerSuccess"));
      } else {
        if (
          Array.isArray(res?.data?.message) &&
          res?.data?.message.some((m) =>
            m.toLowerCase().includes("email already exists")
          )
        ) {
          toastError(getTranslation("emailExist"));
        } else {
          toastError(getTranslation("invalidCredentials"));
        }
      }
    } catch (err) {
      console.error("Register error:", err);
      toastError(getTranslation("somethingWentWrong"));
    } finally {
      setIsLoading(false);
    }
  };
  // Google ile kayit/giris: backend e-postayi eslestirir, yoksa hesabi olusturur
  // (registration_source "memeet"); sonrasi e-posta girisiyle ayni.
  const handleGoogleLogin = async (idToken) => {
    if (!idToken || !token) {
      toastError(getTranslation("somethingWentWrong"));
      return;
    }
    setIsLoading(true);
    try {
      const res = await Services.LoginWithGoogle(token, idToken);
      const userData = res?.data?.result || res?.data;
      const userToken = userData?.token || userData?.accessToken || userData?.authToken || userData?.userToken;
      if (res?.data?.status && userToken) {
        login(userData, userToken, true);
        navigate(postAuthPath, { replace: true });
        return;
      }
      console.error("[GoogleLogin] No token in response:", res?.data);
      toastError(getTranslation("googleLoginFailed"));
    } catch (err) {
      console.error("[GoogleLogin] error:", err, err?.response?.data);
      toastError(getTranslation("googleLoginFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToLogin = () => {
    if (location.state) {
      navigate("/login", { state: location.state });
      return;
    }
    navigate("/login");
  };

  // Success screen
  if (isSuccess) {
    return (
      <div className="auth-container">
        <div className="auth-left-circle"></div>
        <div className="auth-right-circle"></div>

        <div className="auth-success-container">
          <div className="auth-success-card">
            <FaCheckCircle className="auth-success-icon" />
            <h1 className="auth-success-title">{getTranslation("registerSuccessTitle")}</h1>
            <p className="auth-success-message">{getTranslation("checkEmail")}</p>
            <p className="auth-success-email">{email}</p>
            <button
              className="auth-button"
              onClick={handleGoToLogin}
            >
              {getTranslation("goToLogin")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-left-circle"></div>
      <div className="auth-right-circle"></div>

      <button
        onClick={handleGoToLogin}
        className="auth-redirect-button"
      >
        {getTranslation("login")} <TbLogin2 size={20} />
      </button>

      <div className="auth-language-selector">
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="auth-lang-select"
        >
          {availableLanguages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.name}
            </option>
          ))}
        </select>
      </div>

      <div className="auth-left-section">
        <div className="auth-left-inner">
          <span className="auth-left-text">
            {getTranslation("manageWorkforce")}{" "}
            <span className="auth-left-highlight">{getTranslation("oneSpace")}</span>
          </span>
          <span className="auth-left-subtitle">
            {getTranslation("signInJourney")}
          </span>
        </div>
      </div>

      <div className="auth-right-section">
        <div className="auth-right-inner">
          <div className="auth-logo">
            <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M4 7a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z" />
              <path d="M17 10l4-2v8l-4-2v-4z" />
            </svg>
            <span className="auth-logo-text">MeMeet</span>
          </div>

          <span className="auth-right-subtitle">
            {getTranslation("letsManage")}
          </span>

          <form className="auth-form" onSubmit={handleRegister}>
            <label className="auth-input-label">{getTranslation("name")}*</label>
            <div className="auth-input-row">
              <input
                className="auth-input"
                type="text"
                placeholder={getTranslation("name")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="given-name"
              />
              <input
                className="auth-input"
                type="text"
                placeholder={getTranslation("lastname")}
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
                autoComplete="family-name"
              />
            </div>

            <label className="auth-input-label">{getTranslation("email")}*</label>
            <input
              className="auth-input"
              type="email"
              placeholder={getTranslation("email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <div className="auth-password-info">
              <p className="auth-password-info-text">
                A secure password will be automatically generated and sent to your email.
              </p>
            </div>

            <button type="submit" className="auth-button" disabled={isLoading || isTokenLoading || !token}>
              {isLoading || isTokenLoading ? <div className="auth-spinner"></div> : getTranslation("getStarted")}
            </button>
          </form>

          <div className="auth-divider">
            <span className="auth-divider-line"></span>
            <span className="auth-divider-text">{getTranslation("or")}</span>
            <span className="auth-divider-line"></span>
          </div>

          <div className="auth-social-buttons" ref={socialRef}>
            <GoogleLogin
              onSuccess={(res) => handleGoogleLogin(res?.credential)}
              onError={() => toastError(getTranslation("googleLoginFailed"))}
              theme="filled_black"
              text="signup_with"
              shape="rectangular"
              size="large"
              width={socialWidth}
              locale={language}
            />
          </div>

          <span className="auth-terms-text">
            {getTranslation("termsText")}
          </span>

          <span className="auth-switch-text">
            {getTranslation("alreadyHaveAccount")}{" "}
            <span onClick={handleGoToLogin} className="auth-link">
              {getTranslation("login")}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
